<template>
  <div
    class="contact-review card p-4 mb-3"
    novalidate
    v-if="account && company"
  >
    <AccountFormFields
      :modelValue="account"
      :fields="accountFields"
      :v$="v$.accountData"
    />

    <CompanySearchAndForm
      :fields="companyFields"
      :v$="v$.companyData"
      :is-data-pristine="isCompanyDataPristine"
      v-model="company"
      @quickfill-select="handleQuickFill"
    >
      <!-- Pass the country-specific quickfill component -->
      <template #quickfill="{ handleSelect }">
        <CompanyQuickFill @select="handleSelect" />
      </template>
    </CompanySearchAndForm>
  </div>

  <SignupNavigationButtons
    :showBackButton="fiSignupStore.hasPreviousStep"
    :isStepValid="fiSignupStore.isCurrentStepValid"
    :isLoading="isLoading"
    @back="fiSignupStore.previous()"
    @next="fiSignupStore.next().catch(globalErrorHandler)"
  />
</template>

<script setup lang="ts">
import { useFISignupStore } from "@/signup/fiSignupStore";
import { useSignupStore } from "@/signup/signup";
import { Company, ContactFieldType } from "@/types";
import useVuelidate from "@vuelidate/core";
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import CompanyQuickFill from "../../components/CompanyQuickFill.vue";

import AccountFormFields from "@/signup/common/AccountFormFields.vue";
import CompanySearchAndForm from "@/signup/common/CompanySearchAndForm.vue";
import SignupNavigationButtons from "@/signup/common/SignupNavigationButtons.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

const fiSignupStore = useFISignupStore();
const { isLoading, stepConfigurations } = storeToRefs(fiSignupStore);

const signupStore = useSignupStore();
const { account, company } = storeToRefs(signupStore);
const { updateCompany } = signupStore;

const validationConfig = computed(
  () => stepConfigurations.value["contact-details"]?.validation
);
const rules = computed(() => validationConfig.value?.rules);

const accountFields = computed<ContactFieldType[]>(() => [
  {
    label: t("account.name"),
    id: "account-name",
    model: "name",
    type: "text",
    editable: true,
    validators: rules.value?.name,
  },
  {
    label: t("account.phone_number"),
    id: "account-phone-number",
    model: "phone_number",
    type: "tel",
    editable: true,
    validators: rules.value?.phone_number,
  },
  {
    label: t("account.registry_code"),
    id: "account-registry-code",
    model: "registry_code",
    type: "text",
    editable: true,
    validators: rules.value?.registry_code,
  },
]);

const companyFields = computed<ContactFieldType[]>(() => [
  {
    label: t("company.name"),
    id: "company-name",
    model: "name",
    type: "text",
    editable: true,
    validators: rules.value?.company?.name,
  },
  {
    label: t("company.registry_code"),
    id: "company-registry-code",
    model: "registry_code",
    type: "text",
    editable: true,
    validators: rules.value?.company?.registry_code,
  },
  {
    label: t("company.vat"),
    id: "company-vat",
    model: "vat",
    type: "text",
    editable: true,
    validators: rules.value?.company?.vat,
  },
  {
    label: t("company.address"),
    id: "company-address",
    model: "address",
    type: "text",
    editable: true,
    validators: rules.value?.company?.address,
  },
]);

// Initialize validation with safe fallbacks
// Pass the validation rules and data reactively to Vuelidate
const v$ = useVuelidate(
  computed(() => ({
    accountData: rules.value,
    companyData: rules.value?.company,
  })),
  {
    accountData: account,
    companyData: company,
  }
);

// Check if we should show the form initially based on existing data
const isCompanyDataPristine = computed(() => {
  if (!company.value) return true;

  // Use Vuelidate's dirty state
  return (
    v$.value.companyData?.$dirty === false &&
    !company.value.name &&
    !company.value.registry_code &&
    !company.value.vat &&
    !company.value.address
  );
});

const handleQuickFill = (newCompany: Company) => {
  if (!company.value) return;

  updateCompany(newCompany);

  // Manually trigger validation after merge
  v$.value.companyData?.$touch();
};

// Ensure we have a company immediately and fetch company data
onMounted(async () => {
  await signupStore.createCompanyIfMissing();
});
</script>

<style scoped lang="scss">
@media (max-width: 575.98px) {
  h3 {
    font-size: 1.2rem;
  }
}
</style>
